import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router';

import Header from '../components/header';
import Footer from '../components/Footer';
import InfoBar from '../components/InfoBar';
import { WishlistTable } from '../components/WishlistTable';
import api from '../util/api';
import { hasRoleWishlistReader, hasRoleWishlistEditor } from '../util/auth';
import './Wishlist.scss';

export function Wishlist() {
    const [error, setError] = useState('');
    const [cart, setCart] = useState([]); // wishlist items that have not been ordered yet
    const [ordered, setOrdered] = useState([]); // wishlist items that have been ordered
    const [isWishlistReader, setIsWishlistReader] = useState(false);
    const [isWishlistEditor, setIsWishlistEditor] = useState(false);
    const [prices, setPrices] = useState({});

    const COLUMNS = [
        {
            id: 'shortName',
            Header: 'Typ',
            accessorKey: 'shortName',
        }, {
            id: 'fullName',
            Header: 'Bezeichnung',
            accessorKey: 'fullName',
        }, {
            id: 'orderCode',
            Header: 'Bestellnummer',
            accessorKey: 'orderCode',
        }, {
            id: 'price',
            Header: 'Preis',
            accessorKey: 'shortName',
            renderFunc: (shortName) => {
                const price = prices[shortName];
                return price !== undefined ? `${price.toFixed(2)} €` : '?';
            },
        }, {
            id: 'location',
            Header: 'Ort',
            accessorKey: 'location',
        }, {
            id: "replaces",
            Header: "Ersetzt",
            accessorKey: "replaces",
            renderFunc: (value) => {
                return <a href={ "/#/show/" + value }>{value}</a>
            },
        }, {
            id: "addedBy",
            Header: "Von",
            accessorKey: "addedBy",
        },
    ];

    function updateWishlist() {
        api.getWishlist()
            .then((response) => {
                setCart(response.filter((item) => !item.orderedOn));
                setOrdered(response.filter((item) => item.orderedOn));
            })
            .catch((error) => {
                setError(`Failed to fetch Wishlist: ${error}`);
            });
    }

    function removeWishlistItem(wishId) {
        let verify = window.confirm('Wirklich löschen?');
        if (verify) {
            api.removeFromWishlist(wishId)
                .then(() => {
                    updateWishlist();
                })
                .catch((error) => {
                    setError(`Failed to remove item from Wishlist: ${error}`);
                });
        }
    }   

    function markAsOrdered(wishId) {
        if (window.confirm('Als bestellt markieren?')) {            
            api
                .markAsOrdered(wishId)
                .then(() => {
                    updateWishlist();
                })
                .catch((error) => {
                    setError(`Failed to mark item as ordered: ${error}`);
                });
        }
    }

    const navigate = useNavigate();

    function showWishlistItem(wishId) {
        navigate('/wishlist/' + wishId);
    }

    function commitWishlistOrder(wishId) {
        navigate('/wishlist/' + wishId + '/commit');
    }

    useEffect(() => {
        hasRoleWishlistReader().then((value) => {
            setIsWishlistReader(value);
            if (value) {
                updateWishlist();
            } else {
                setError('You are not allowed to view this page');
            }
        });
        hasRoleWishlistEditor().then(setIsWishlistEditor);
        api.getPrices().then(list => setPrices(Object.fromEntries(list.map(p => [p.shortName, p.price])))).catch(() => {});
    }, []);

    return (
        <div className="wishlist">
            <Header />
            <div className="content">
                <InfoBar show={error !== ""} text={error} title="Fehler" color="#F00" />
                <div className="cart">
                    <div className="d-flex align-items-center mb-2">
                        <div style={{ flex: 1 }} />
                        <h3 className="mb-0">Im Warenkorb</h3>
                        <div style={{ flex: 1 }} className="d-flex justify-content-end">
                            {isWishlistEditor && (
                                <Button size="sm" variant="outline-secondary" className="mx-3" onClick={() => navigate('/wishlist/new')}>
                                    Hinzufügen
                                </Button>
                            )}
                        </div>
                    </div>
                    <WishlistTable
                        columns={COLUMNS}
                        items={cart}
                        requestWishlistUpdate={updateWishlist}
                        onShow={showWishlistItem}
                        onRemove={removeWishlistItem}
                        onMarkAsOrdered={markAsOrdered}
                        showShow={isWishlistReader}
                        showRemove={isWishlistEditor}
                        showMarkAsOrdered={isWishlistEditor}
                    />
                </div>
                <div className='ordered'>
                    <h3>Bestellt</h3>
                    <WishlistTable
                        columns={COLUMNS}
                        items={ordered}
                        requestWishlistUpdate={updateWishlist}
                        onCommit={commitWishlistOrder}
                        onShow={showWishlistItem}
                        onRemove={removeWishlistItem}
                        showShow={isWishlistReader}
                        showCommit={isWishlistEditor}
                        showRemove={isWishlistEditor}
                    />
                </div>
            </div>
            <Footer />
        </div>
    );
}
